import { useState, useEffect } from 'react'
import {
  X, Check, AlertTriangle, Loader2,
  TrendingUp, MoveVertical, RefreshCw, DollarSign, Ban, Shield, Target, Plus,
} from 'lucide-react'
import { hasLiveSetupOrderEvidence } from '../lib/setupRepairPlan.js'

const ACTION_LABELS = {
  REPLACE: 'Replace Stop Orders',
  PLACE: 'Market Sell Orders',
  CANCEL: 'Cancel Orders',
  TAKE_PROFIT: 'Take 1/3 Profit',
  FLATTEN: 'Flatten Position at Market',
  STAGE_ORDERS: 'Send Orders',
  ADD_TRANCHE: 'Add Tranche',
  REPLACE_SETUP: 'Replace Setup Orders',
  REPAIR_SETUP: 'Repair Missing Setup Orders',
}

// EF-125 Part 3: action-typed icon at the top-left of the modal so the
// glyph communicates what is about to happen. Same accent-color system
// as before — only the glyph changes; FLATTEN keeps its ember treatment.
const ICON_FOR_ACTION = {
  ADD_TRANCHE: TrendingUp,
  STAGE_ORDERS: TrendingUp,
  REPLACE: MoveVertical,
  REPLACE_SETUP: RefreshCw,
  REPAIR_SETUP: RefreshCw,
  TAKE_PROFIT: DollarSign,
  CANCEL: Ban,
  FLATTEN: AlertTriangle,
}

function getActionIcon(action, { isPlacingStops, isPlacingLimits }) {
  if (action === 'PLACE' && isPlacingStops) return Shield
  if (action === 'PLACE' && isPlacingLimits) return Target
  if (action === 'PLACE') return Plus
  return ICON_FOR_ACTION[action] || AlertTriangle
}

// PLACE is a generic action — the actual title depends on what kind of
// orders are being placed (STOP/LIMIT/MARKET). Without this the modal
// shows "MARKET SELL ORDERS" for a stop placement, which is wrong and
// gives the user no chance to verify the stop price before confirming.
function deriveModalTitle(action, orders) {
  if (action === 'REPLACE') {
    // EF-187a: TP-move shares the REPLACE action with stop-move; title should
    // reflect what's actually being replaced.
    const isTarget = orders.some(o => o.oldTarget != null || o.newTarget != null)
    return isTarget ? 'Replace Take Profit Orders' : 'Replace Stop Orders'
  }
  if (action !== 'PLACE') return ACTION_LABELS[action] || action
  const types = new Set(orders.map(o => (o.orderType || 'MARKET').toUpperCase()))
  if (types.size === 1) {
    const [t] = [...types]
    if (t === 'STOP') return 'Stop Sell Orders'
    if (t === 'LIMIT') return 'Limit Sell Orders'
    if (t === 'MARKET') return 'Market Sell Orders'
  }
  return 'Sell Orders'
}

// Returns the single price shared across all rows, or null if mixed/missing.
// Used to show a header summary line ("Stop Price: $X") when uniform.
function uniformPrice(orders, key) {
  const prices = orders
    .map(o => (o[key] != null ? Number(o[key]) : null))
    .filter(v => v != null && v > 0)
  if (prices.length === 0 || prices.length !== orders.length) return null
  const first = prices[0]
  return prices.every(p => Math.abs(p - first) < 0.0001) ? first : null
}

// OTR-04: detect Schwab reauth errors in the results array. Structured
// signal (errorCode) from /api/oms/execute is the primary detector; the
// regex is a defensive fallback for legacy non-OTR-03 surfaces.
//
// EF-182: narrowed to Schwab-specific markers ONLY. The previous regex
// /invalid.?jwt|token.*expired|schwab.*auth.*expired/i false-positived on
// GoTrue's "invalid JWT: ... token is expired" error from an expired
// Supabase session (EF-182 root-cause). Generic JWT errors are now caught
// by the structured errorCode path (SESSION_EXPIRED); this regex only
// catches Schwab legacy surfaces that haven't been migrated.
const REAUTH_LEGACY_RE = /schwab.*(auth.*expired|invalid_grant|reauth)/i

// EF-182: file-local banner shared by the two disambiguated auth surfaces
// (SESSION_EXPIRED "Reload page" + SCHWAB_AUTH_EXPIRED "Reconnect Schwab").
// Same chrome — title, optional action button, collapsed technical details.
function ReauthBanner({ title, actionLabel, onAction, results }) {
  return (
    <div className="px-6 py-3 border-t border-border bg-ember/5">
      <div className="font-mono text-[11px] text-ember font-semibold">
        {title}
      </div>
      {onAction && (
        <button
          onClick={onAction}
          className="mt-2 font-mono text-[10px] font-semibold px-3 py-1.5 rounded-md border border-ember/40 text-ember hover:bg-ember/10 transition-colors"
        >
          {actionLabel}
        </button>
      )}
      <details className="mt-2">
        <summary className="font-mono text-[9px] text-inkMuted cursor-pointer">
          Technical details
        </summary>
        <div className="mt-1 font-mono text-[9px] text-inkMuted whitespace-pre-wrap break-words">
          {results.filter(r => !r?.success).map(r => r?.error).filter(Boolean).join('\n')}
        </div>
      </details>
    </div>
  )
}

function isAuthResultError(result) {
  return result?.errorCode === 'SCHWAB_AUTH_EXPIRED'
    || result?.errorCode === 'SESSION_EXPIRED'
    || (typeof result?.error === 'string' && REAUTH_LEGACY_RE.test(result.error))
}

function visibleResultError(result) {
  if (!result || result.success || isAuthResultError(result)) return null
  return result.error || 'Broker rejected this order. Check Schwab canceled orders for the broker reason.'
}

function aggregateOrderResult({ results, order, action }) {
  if (!Array.isArray(results)) return null
  const sameAccountResults = results.filter(r => (
    String(r.account_number ?? r.accountNumber ?? '') === String(order.accountNumber ?? '')
  ))
  let rowResults = sameAccountResults.filter(r => {
    const sameAccount =
      String(r.account_number ?? r.accountNumber ?? '') === String(order.accountNumber ?? '')
    if (!sameAccount) return false
    if (!order.bracketGroupId) return true
    // REPLACE_SETUP is batched CANCEL + PLACE. Cancel-batch results have no
    // bracketGroupId; include same-account cancel rows so a failed precondition
    // stays visible on the affected setup row.
    if (action === 'REPLACE_SETUP' || action === 'REPAIR_SETUP') {
      return r.bracketGroupId == null || r.bracketGroupId === order.bracketGroupId
    }
    return r.bracketGroupId === order.bracketGroupId
  })
  if (
    rowResults.length === 0 &&
    order.bracketGroupId &&
    (action === 'STAGE_ORDERS' || action === 'ADD_TRANCHE') &&
    sameAccountResults.length > 0
  ) {
    rowResults = sameAccountResults
  }
  if (rowResults.length === 0) return null
  const failed = rowResults.find(r => !r.success)
  const message = rowResults.find(r => r.success && r.message)?.message || null
  return {
    success: rowResults.every(r => r.success),
    error: failed?.error || null,
    errorCode: failed?.errorCode,
    message,
    manualCleanupRequired: rowResults.some(r => r.manualCleanupRequired === true),
    orphanedOrderIds: rowResults
      .filter(r => r.manualCleanupRequired === true)
      .map(r => r.orphanedOrderId)
      .filter(Boolean),
  }
}

export default function OrderConfirmationModal({
  open,
  onClose,
  onConfirm,
  loading = false,
  action,
  symbol,
  orders = [],
  results = null,
  onReconnect,
  onPostReconnectRefresh, // eslint-disable-line no-unused-vars
  onRepairMissingOrders,
}) {
  // Account selection — all checked by default, reset when modal opens with new orders
  const [selectedAccounts, setSelectedAccounts] = useState(new Set())

  useEffect(() => {
    if (open && orders.length > 0) {
      setSelectedAccounts(new Set(orders.map(o => o.accountNumber)))
    }
  }, [open, orders.length]) // eslint-disable-line react-hooks/exhaustive-deps

  // EF-159d: ESC closes the modal — standard modal UX. ActionPanel's
  // ESC handler from EF-134e-fix2 only covers the Move Stop / Place
  // Stop expanded sections; the confirmation modal lives in
  // PositionWorkspace and needs its own listener. stopPropagation so
  // ActionPanel's listener doesn't ALSO fire and try to collapse a
  // (now-irrelevant) expanded quick-action section.
  useEffect(() => {
    if (!open) return
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose()
        e.stopPropagation()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose])

  // EF-182: banner state derived from structured errorCode first, regex
  // fallback second. Two distinct auth-failure surfaces:
  //   - SCHWAB_AUTH_EXPIRED → Schwab token dead → "Reconnect Schwab"
  //   - SESSION_EXPIRED     → expired Supabase browser session → "Reload page"
  const hasSchwabReauth = !!results?.some(r =>
    r?.errorCode === 'SCHWAB_AUTH_EXPIRED' ||
    (typeof r?.error === 'string' && REAUTH_LEGACY_RE.test(r.error))
  )
  const hasSessionExpired = !!results?.some(r => r?.errorCode === 'SESSION_EXPIRED')
  const canReconnect = hasSchwabReauth && typeof onReconnect === 'function'

  if (!open) return null

  const toggleAccount = (accountNumber) => {
    setSelectedAccounts(prev => {
      const next = new Set(prev)
      if (next.has(accountNumber)) next.delete(accountNumber)
      else next.add(accountNumber)
      return next
    })
  }

  const allSucceeded = results && results.every(r => r.success)
  const canRepairMissingOrders = action === 'STAGE_ORDERS'
    && typeof onRepairMissingOrders === 'function'
    && hasLiveSetupOrderEvidence(results)
    && results?.some(r => r?.success !== true)
  const isDestructive = action === 'FLATTEN'
  const accentColor = isDestructive ? 'rgb(var(--color-ember))' : '#c4953a'
  const selectableAccountCount = new Set(orders.map(o => o.accountNumber).filter(Boolean)).size
  const selectedCount = [...selectedAccounts].filter(accountNumber => accountNumber != null).length
  const showCheckboxes = orders.length > 1 && !results

  const modalTitle = deriveModalTitle(action, orders)

  // PLACE rows expose stopPrice (STOP) or price (LIMIT). Surface a header
  // summary when uniform; the per-row PRICE column handles mixed prices.
  const isPlacingStops = action === 'PLACE' && orders.some(o => (o.orderType || '').toUpperCase() === 'STOP')
  const isPlacingLimits = action === 'PLACE' && orders.some(o => (o.orderType || '').toUpperCase() === 'LIMIT')
  const ActionIcon = getActionIcon(action, { isPlacingStops, isPlacingLimits })
  const uniformStopPrice = isPlacingStops ? uniformPrice(orders, 'stopPrice') : null
  const uniformLimitPrice = isPlacingLimits ? uniformPrice(orders, 'price') : null

  // REPLACE has oldStop/newStop per row (stop-move) OR oldTarget/newTarget
  // per row (EF-187a take-profit-move). Show uniform old → new in the header
  // when every row shares the same prices.
  const isTargetReplace = action === 'REPLACE' && orders.some(o => o.oldTarget != null || o.newTarget != null)
  const uniformReplaceOld = action === 'REPLACE'
    ? uniformPrice(orders, isTargetReplace ? 'oldTarget' : 'oldStop')
    : null
  const uniformReplaceNew = action === 'REPLACE'
    ? uniformPrice(orders, isTargetReplace ? 'newTarget' : 'newStop')
    : null
  const replaceHeaderLabel = isTargetReplace ? 'Target' : 'Stop'

  // Show the per-row PRICE column whenever ANY row carries a stop or limit.
  const showPriceColumn = action === 'PLACE' && orders.some(o =>
    (o.stopPrice != null && Number(o.stopPrice) > 0) ||
    (o.price != null && Number(o.price) > 0)
  )
  const failedOrderDetails = results
    ? orders
      .map((order) => {
        const result = aggregateOrderResult({ results, order, action })
        const error = visibleResultError(result)
        return error ? { order, error } : null
      })
      .filter(Boolean)
    : []

  const handleConfirm = () => {
    onConfirm(selectedAccounts)
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-3" onClick={onClose}>
      <div
        className="bg-canvas border border-border rounded-xl shadow-2xl w-full max-w-lg max-h-[calc(100dvh-1.5rem)] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
        data-testid="order-confirmation-dialog"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <ActionIcon size={18} style={{ color: accentColor }} />
            <div>
              <div className="font-mono font-black text-sm uppercase tracking-wide text-ink">
                {modalTitle}
              </div>
              <div className="font-mono text-lg font-black text-ink">{symbol}</div>
              {uniformStopPrice != null && (
                <div className="font-mono text-xs text-inkMuted mt-0.5">
                  Stop Price: <span className="text-ink font-medium">${uniformStopPrice.toFixed(2)}</span>
                </div>
              )}
              {uniformLimitPrice != null && (
                <div className="font-mono text-xs text-inkMuted mt-0.5">
                  Limit Price: <span className="text-ink font-medium">${uniformLimitPrice.toFixed(2)}</span>
                </div>
              )}
              {uniformReplaceOld != null && uniformReplaceNew != null && (
                <div className="font-mono text-xs text-inkMuted mt-0.5">
                  {replaceHeaderLabel}: <span className="text-inkMuted">${uniformReplaceOld.toFixed(2)}</span>
                  <span className="mx-1">{'→'}</span>
                  <span className="text-ink font-medium">${uniformReplaceNew.toFixed(2)}</span>
                </div>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-inkMuted hover:text-ink transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Order Details Table */}
        <div className="px-6 py-4 overflow-y-auto min-h-0" data-testid="order-confirmation-scroll-body">
          <table className="w-full font-mono text-xs">
            <thead>
              <tr className="text-[10px] tracking-widest uppercase text-inkMuted">
                {showCheckboxes && <th className="text-left py-2 w-6"></th>}
                <th className="text-left py-2">Account</th>
                <th className="text-right py-2">Shares</th>
                {action === 'REPLACE' && <th className="text-right py-2">{replaceHeaderLabel}</th>}
                {action === 'PLACE' && showPriceColumn && <th className="text-right py-2">Price</th>}
                {action === 'PLACE' && <th className="text-right py-2">Type</th>}
                {action === 'TAKE_PROFIT' && <th className="text-right py-2">Stop Adj.</th>}
                {action === 'FLATTEN' && <th className="text-right py-2">Stops</th>}
                {(action === 'STAGE_ORDERS' || action === 'ADD_TRANCHE' || action === 'REPLACE_SETUP' || action === 'REPAIR_SETUP') && <th className="text-right py-2">Details</th>}
                {action === 'CANCEL' && <th className="text-right py-2">Order</th>}
                {results && <th className="text-right py-2">Status</th>}
              </tr>
            </thead>
            <tbody>
              {orders.map((order, i) => {
                const wasSelected = selectedAccounts.has(order.accountNumber)
                // EF-189 v3: a single staging row can be backed by two
                // Schwab results (target + runner tranches sharing a
                // bracketGroupId). The pre-v3 `results.find()` matched
                // only the first sibling and masked the other's failure
                // — that left half-placed brackets looking healthy in the
                // modal. Aggregate by bracketGroupId so the row shows
                // failure unless EVERY member of the group succeeded,
                // and surface orphan-cleanup metadata when present.
                const result = aggregateOrderResult({ results, order, action })
                const rowError = visibleResultError(result)
                const isSelected = wasSelected
                const rowOpacity = showCheckboxes && !isSelected ? 'opacity-40' : ''
                return (
                  <tr
                    key={[
                      order.accountNumber,
                      order.orderId,
                      order.bracketGroupId,
                      order.orderLabel,
                      order.price,
                      order.stopPrice,
                      order.targetPrice,
                      i,
                    ].filter(v => v != null && v !== '').join(':')}
                    className={`border-t border-border/50 ${showCheckboxes ? 'cursor-pointer hover:bg-canvas2/50' : ''} ${rowOpacity}`}
                    onClick={showCheckboxes ? () => toggleAccount(order.accountNumber) : undefined}
                  >
                    {showCheckboxes && (
                      <td className="py-2.5 text-left">
                        <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center transition-colors ${
                          isSelected
                            ? 'bg-[#c4953a] border-[#c4953a]'
                            : 'border-border bg-canvas'
                        }`}>
                          {isSelected && <Check size={10} className="text-white" strokeWidth={3} />}
                        </div>
                      </td>
                    )}
                    <td className="py-2.5 text-left text-ink">
                      {order.accountName || `****${order.accountNumber?.slice(-4)}`}
                    </td>
                    <td className="py-2.5 text-right text-ink">{order.quantity}</td>
                    {action === 'REPLACE' && (() => {
                      const oldVal = isTargetReplace ? order.oldTarget : order.oldStop
                      const newVal = isTargetReplace ? order.newTarget : order.newStop
                      return (
                        <td className="py-2.5 text-right text-ink">
                          <span className="text-inkMuted">${oldVal?.toFixed(2)}</span>
                          <span className="text-inkMuted mx-1">{'\u2192'}</span>
                          <span className="text-ink font-semibold">${newVal?.toFixed(2)}</span>
                        </td>
                      )
                    })()}
                    {action === 'PLACE' && showPriceColumn && (
                      <td className="py-2.5 text-right text-ink">
                        {(() => {
                          const t = (order.orderType || 'MARKET').toUpperCase()
                          if (t === 'STOP' && order.stopPrice != null) return `$${Number(order.stopPrice).toFixed(2)}`
                          if (t === 'LIMIT' && order.price != null) return `$${Number(order.price).toFixed(2)}`
                          return <span className="text-inkMuted">—</span>
                        })()}
                      </td>
                    )}
                    {action === 'PLACE' && (
                      <td className="py-2.5 text-right text-ink">
                        {(order.orderType || 'MARKET').toUpperCase() === 'MARKET' ? 'MKT' : (order.orderType || '').toUpperCase()}
                        {order.side === 'SELL' ? ' SELL' : ' BUY'}
                      </td>
                    )}
                    {action === 'TAKE_PROFIT' && (
                      <td className="py-2.5 text-right text-ink">
                        <div className="text-[10px] text-inkMuted flex flex-col items-end gap-0.5">
                          {Array.isArray(order.stopAdjustments) && order.stopAdjustments.length > 0
                            ? order.stopAdjustments.map(adj => (
                              <div key={`${adj.orderId ?? adj.stopPrice}-${adj.oldQty}-${adj.newQty}`}>
                                Stop: {adj.oldQty} {'\u2192'} {adj.newQty} @ ${Number(adj.stopPrice).toFixed(2)}
                              </div>
                            ))
                            : order.hasStopReduction
                              ? `Stop: ${order.oldStopQty} \u2192 ${order.newStopQty} @ $${order.stopPrice?.toFixed(2)}`
                              : 'No stop to adjust'
                          }
                        </div>
                      </td>
                    )}
                    {action === 'FLATTEN' && (
                      <td className="py-2.5 text-right text-ink">
                        <div className="text-[10px] text-inkMuted">
                          {order.stopsToCancel > 0
                            ? `${order.stopsToCancel} stop${order.stopsToCancel > 1 ? 's' : ''} will cancel`
                            : 'No stops to cancel'
                          }
                        </div>
                      </td>
                    )}
                    {(action === 'STAGE_ORDERS' || action === 'ADD_TRANCHE' || action === 'REPLACE_SETUP' || action === 'REPAIR_SETUP') && (
                      <td className="py-2.5 text-right text-[10px] text-inkMuted">
                        <div className="flex flex-col items-end gap-0.5">
                          <div>
                            <span className="text-forest/80">
                              {order.entryType === 'On Strength' ? 'Buy Stop' : 'Buy Limit'}
                            </span>{' '}
                            @ ${order.limitPrice?.toFixed(2)} <span className="text-inkMuted/60">{'\u00d7'}</span> {order.quantity}
                          </div>
                          {order.splitBracket && order.targetPrice != null && order.sellQty > 0 ? (
                            <>
                              {/* Mirrors the two Schwab BRACKET_ENTRY tranches: target
                                  tranche's STOP for sellQty + runner tranche's STOP for
                                  runnerQty. sellQty + runnerQty === quantity. */}
                              <div>
                                <span className="text-ember/80">Sell Stop (target)</span>{' '}
                                @ ${order.stopPrice?.toFixed(2)} <span className="text-inkMuted/60">{'\u00d7'}</span> {order.sellQty}
                              </div>
                              <div>
                                <span className="text-ember/80">Sell Limit</span>{' '}
                                @ ${Number(order.targetPrice).toFixed(2)} <span className="text-inkMuted/60">{'\u00d7'}</span> {order.sellQty}{' '}
                                <span className="text-inkMuted/60">(1/3 target tranche)</span>
                              </div>
                              <div>
                                <span className="text-ember/80">Sell Stop (runner)</span>{' '}
                                @ ${order.stopPrice?.toFixed(2)} <span className="text-inkMuted/60">{'\u00d7'}</span> {order.runnerQty ?? (order.quantity - order.sellQty)}{' '}
                                <span className="text-inkMuted/60">(remains live after target fills)</span>
                              </div>
                            </>
                          ) : (
                            <>
                              <div>
                                <span className="text-ember/80">Sell Stop</span>{' '}
                                @ ${order.stopPrice?.toFixed(2)} <span className="text-inkMuted/60">{'\u00d7'}</span> {order.quantity}
                              </div>
                              {order.targetPrice != null && order.sellQty > 0 && (
                                <div>
                                  <span className="text-ember/80">Sell Limit</span>{' '}
                                  @ ${Number(order.targetPrice).toFixed(2)} <span className="text-inkMuted/60">{'\u00d7'}</span> {order.sellQty}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    )}
                    {action === 'CANCEL' && (
                      <td className="py-2.5 text-right text-[10px] text-inkMuted">
                        <span className={`font-semibold ${
                          order.orderLabel === 'Limit Buy' || order.orderLabel === 'Stop Buy' ? 'text-forest/80'
                          : order.orderLabel === 'Stop Loss' ? 'text-ember/80'
                          : order.orderLabel === 'Take Profit' ? 'text-forest/80'
                          : 'text-ink/60'
                        }`}>
                          {order.orderLabel || 'Order'}
                        </span>
                        {order.price > 0 && (
                          <span className="text-inkMuted ml-1">
                            @ ${order.price.toFixed(2)}
                          </span>
                        )}
                      </td>
                    )}
                    {results && (
                      <td className="py-2.5 text-right">
                        {!wasSelected
                          ? <span className="font-mono text-[10px] text-inkMuted">Skipped</span>
                          : result?.success
                            ? <Check size={14} className="inline text-forest" />
                            : (
                              <div>
                                <X size={14} className="inline text-ember" />
                                {rowError && (
                                  <div className="font-mono text-[9px] text-ember/70 mt-0.5 text-right break-words" title={result.error}>
                                    {rowError}
                                  </div>
                                )}
                              </div>
                            )}
                        {result?.success && result?.message && (
                          <div className="font-mono text-[9px] text-forest/70 mt-0.5 text-right break-words" title={result.message}>
                            {result.message}
                          </div>
                        )}
                        {/* EF-189 v3: rollback failed — a live orphan order remains
                            at Schwab and the user must cancel it manually. Always
                            renders for the affected row regardless of overall
                            success state, since the placement was technically
                            successful but the bracket is half-placed. */}
                        {result?.manualCleanupRequired && (
                          <div className="mt-1 font-mono text-[9px] text-ember bg-ember/10 border border-ember/30 rounded px-1.5 py-1 text-right break-words">
                            Manual cleanup required — orphan order(s) at Schwab: {result.orphanedOrderIds?.join(', ')}. Cancel via Working Orders panel.
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
          {failedOrderDetails.length > 0 && (
            <div
              className="mt-3 rounded-lg border border-ember/30 bg-ember/5 px-3 py-2"
              data-testid="order-failure-details"
            >
              <div className="font-mono text-[10px] font-semibold uppercase tracking-widest text-ember">
                Failure details
              </div>
              <div className="mt-1 space-y-1">
                {failedOrderDetails.map(({ order, error }) => (
                  <div
                    key={`${order.accountNumber}-${error}`}
                    className="grid grid-cols-[110px_1fr] gap-2 font-mono text-[10px] text-inkMuted"
                  >
                    <span className="text-ink">{order.accountName || `****${order.accountNumber?.slice(-4)}`}</span>
                    <span className="break-words text-ember/80">{error}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* EF-182: two disambiguated auth banners. SESSION_EXPIRED renders
            FIRST and takes precedence — without a valid Supabase session the
            Schwab reconnect flow can't complete anyway, so the user must
            reload before anything else works. Both suppress the per-row raw
            rendering above. */}
        {hasSessionExpired && (
          <ReauthBanner
            title="Your sign-in expired. Reload the page to continue."
            actionLabel="Reload page"
            onAction={() => window.location.reload()}
            results={results}
          />
        )}
        {!hasSessionExpired && hasSchwabReauth && (
          <ReauthBanner
            title="Your Schwab session expired. Reconnect to retry."
            actionLabel="Reconnect Schwab"
            onAction={canReconnect ? onReconnect : null}
            results={results}
          />
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border flex shrink-0 items-center justify-between">
          <div className="font-mono text-[10px] text-inkMuted">
            {results
              ? (allSucceeded
                  ? (isDestructive ? 'Position flattened successfully' : 'All orders executed successfully')
                  : 'Some orders failed \u2014 check details above')
              : isDestructive
                ? `This will close your entire ${symbol} position`
                : `${selectedCount} of ${selectableAccountCount || orders.length} account${(selectableAccountCount || orders.length) !== 1 ? 's' : ''} selected`
            }
          </div>
          <div className="flex items-center gap-3">
            {!results && (
              <>
                <button
                  onClick={onClose}
                  disabled={loading}
                  className="font-mono text-xs px-4 py-2 rounded-lg border border-border text-inkMuted hover:text-ink transition-colors disabled:opacity-40"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={loading || selectedCount === 0}
                  className="font-mono text-xs font-semibold px-4 py-2 rounded-lg transition-colors disabled:opacity-60 flex items-center gap-2"
                  style={{
                    border: `1px solid ${accentColor}`,
                    backgroundColor: isDestructive ? 'rgba(220,38,38,0.1)' : 'rgba(196,149,58,0.1)',
                    color: accentColor,
                  }}
                >
                  {loading && <Loader2 size={12} className="animate-spin" />}
                  {loading ? 'Sending...' : isDestructive ? 'Confirm Flatten' : 'Confirm Orders'}
                </button>
              </>
            )}
            {results && (
              <>
                {canRepairMissingOrders && (
                  <button
                    onClick={onRepairMissingOrders}
                    className="font-mono text-xs font-semibold px-4 py-2 rounded-lg border border-ember/40 bg-ember/10 text-ember hover:bg-ember/20 transition-colors"
                  >
                    Repair Missing Orders
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="font-mono text-xs px-4 py-2 rounded-lg border border-border text-ink hover:bg-canvas2 transition-colors"
                >
                  Close
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
